import { useEffect, useRef } from 'react';
import { calm, dpr, readTokens } from './canvasTokens';

/* ══ O INSTRUMENTO ══
   Um arco por bloco do setup. Anel de marcações, varredura lenta,
   núcleo com a prontidão. Desenhado em hairlines, um único acento.
   Portado do protótipo: a mesma geometria, o mesmo ritmo.          */

const SIZE = 336;
const TAU = Math.PI * 2;

export interface InstrumentHandle { pulse: (block: number) => void }

export function Instrument({ fill, weights, pct, onPct, pulseRef }: {
  fill: number[];
  weights: number[];
  pct: number;
  /** o número exibido sobe junto com os arcos */
  onPct: (shown: number) => void;
  pulseRef: React.MutableRefObject<InstrumentHandle | null>;
}) {
  const cv = useRef<HTMLCanvasElement>(null);
  const target = useRef(fill);
  target.current = fill;
  const goal = useRef(pct);
  goal.current = pct;
  const onPctRef = useRef(onPct);
  onPctRef.current = onPct;

  useEffect(() => {
    const canvas = cv.current!;
    const ctx = canvas.getContext('2d')!;
    const still = calm();
    const seat = () => {
      const d = dpr();
      canvas.width = SIZE * d; canvas.height = SIZE * d;
      ctx.setTransform(d, 0, 0, d, 0, 0);
    };
    seat();

    const pulseAt = target.current.map(() => 0);
    pulseRef.current = { pulse: i => { if (i >= 0 && i < pulseAt.length) pulseAt[i] = performance.now(); } };

    let shown = target.current.map(() => 0);
    let pctShown = 0, lastPct = -1;
    const t0 = performance.now();
    let raf = 0;

    const arc = (r: number, a0: number, a1: number, w: number, color: string, cap: CanvasLineCap = 'butt') => {
      ctx.beginPath(); ctx.arc(SIZE / 2, SIZE / 2, r, a0, a1);
      ctx.lineWidth = w; ctx.strokeStyle = color; ctx.lineCap = cap; ctx.stroke();
    };

    const draw = (t: number) => {
      const C = readTokens();
      ctx.clearRect(0, 0, SIZE, SIZE);
      const cx = SIZE / 2, cy = SIZE / 2;
      const intro = still ? 1 : Math.min(1, (t - t0 - 520) / 1200);
      const e = intro <= 0 ? 0 : 1 - Math.pow(1 - Math.min(intro, 1), 3);

      for (let i = 0; i < 60; i++) {
        const a = -Math.PI / 2 + i / 60 * TAU;
        const long = i % 5 === 0;
        const r1 = 152, r2 = r1 - (long ? 7 : 3);
        if (e <= i / 60 * .8) continue;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2);
        ctx.lineWidth = 1; ctx.strokeStyle = long ? C.strong : C.faint; ctx.stroke();
      }

      const R = 126, GAP = 0.075;
      const n = shown.length;
      for (let i = 0; i < n; i++) {
        const a0 = -Math.PI / 2 + i / n * TAU + GAP / 2;
        const a1 = -Math.PI / 2 + (i + 1) / n * TAU - GAP / 2;
        arc(R, a0, a1, 2.5, C.faint, 'round');
        const p = (shown[i] ?? 0) * e;
        if (p > 0.004) arc(R, a0, a0 + (a1 - a0) * p, 2.5, C.brand, 'round');
        const age = t - pulseAt[i]!;
        if (age >= 0 && age < 760 && pulseAt[i]) {
          const k = 1 - age / 760;
          ctx.save();
          ctx.globalAlpha = k * .55;
          arc(R, a0, a0 + (a1 - a0) * Math.max(p, .03), 2.5 + 9 * k, C.brand, 'round');
          ctx.restore();
        }
      }

      arc(101, 0, TAU, 1, C.faint);

      if (!still) {
        const spin = (t / 16000) * TAU;
        ctx.save();
        ctx.translate(cx, cy); ctx.rotate(spin); ctx.translate(-cx, -cy);
        const steps = 44;
        for (let i = 0; i < steps; i++) {
          const a0 = -Math.PI / 2 + (i / steps) * (TAU * 0.30);
          const a1 = -Math.PI / 2 + ((i + 1) / steps) * (TAU * 0.30) + 0.012;
          ctx.globalAlpha = (i / steps) * 0.45 * e;
          arc(101, a0, a1, 1.5, C.brand);
        }
        ctx.restore();
        ctx.globalAlpha = 1;
      }

      ctx.globalAlpha = e * .9;
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
        ctx.beginPath();
        ctx.moveTo(cx + dx * 84, cy + dy * 84);
        ctx.lineTo(cx + dx * 77, cy + dy * 77);
        ctx.lineWidth = 1; ctx.strokeStyle = C.line; ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };

    const loop = (now: number) => {
      const tgt = target.current;
      shown = shown.map((v, i) => {
        const d = (tgt[i] ?? 0) - v;
        return Math.abs(d) > 0.0015 ? v + d * 0.11 : (tgt[i] ?? 0);
      });
      const g = goal.current;
      pctShown = Math.abs(g - pctShown) > 0.4 ? pctShown + (g - pctShown) * 0.12 : g;
      const p = Math.round(pctShown);
      if (p !== lastPct) { lastPct = p; onPctRef.current(p); }
      draw(now);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onResize = () => seat();
    addEventListener('resize', onResize);
    document.fonts?.ready.then(seat).catch(() => {});
    return () => { cancelAnimationFrame(raf); removeEventListener('resize', onResize); pulseRef.current = null; };
  }, [pulseRef]);

  void weights;
  return <canvas ref={cv} width={SIZE} height={SIZE} aria-hidden="true" />;
}
